import React, { useMemo, useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Pencil,
  Eye,
  Sparkles,
  Building2,
  Briefcase,
  FileText,
  ChevronRight,
  Wand2,
  Stars,
  Download,
  LayoutTemplate,
  Gauge,
  Info,
  Edit3,
  Save,
  Plus,
  List,
  Trash2,
  Copy,
} from "lucide-react";
import axios from "axios";
import jsPDF from "jspdf";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8787";

interface Experience {
  id: string;
  jobTitle: string;
  company: string;
  brief: string;
  bullets: string[];
}

interface Resume {
  id: string;
  name: string;
  targetRole: string;
  template: string;
  experiences: Experience[];
  skills: string[];
  createdAt: number;
}

const fieldBase =
  "w-full rounded-xl border bg-transparent/20 px-4 py-3 outline-none transition focus:ring-2 focus:ring-emerald-400/60 dark:focus:ring-emerald-400/60 placeholder:text-zinc-400/70";

const labelBase = "text-sm font-medium text-zinc-700 dark:text-zinc-200";

const Section = ({ title, icon, children, desc }: any) => (
  <div className="rounded-2xl border border-white/10 bg-white/60 p-5 shadow-[0_8px_30px_rgb(0,0,0,0.06)] backdrop-blur-sm dark:border-white/10 dark:bg-zinc-900/60">
    <div className="mb-4 flex items-center gap-2">
      {icon}
      <h3 className="text-lg font-semibold tracking-tight text-emerald-600 dark:text-emerald-400">
        {title}
      </h3>
    </div>
    {desc && <p className="mb-4 text-sm text-zinc-600 dark:text-zinc-300">{desc}</p>}
    {children}
  </div>
);

const TemplateBadge = ({ active, children, onClick }: any) => (
  <button
    onClick={onClick}
    className={[
      "flex items-center gap-2 rounded-xl border px-3 py-2 text-sm transition",
      active
        ? "border-emerald-400/70 bg-emerald-50 text-emerald-800 shadow-sm dark:bg-emerald-900/30 dark:text-emerald-200"
        : "border-white/10 bg-white/40 text-zinc-700 hover:bg-white/70 dark:bg-zinc-800/40 dark:text-zinc-200 hover:dark:bg-zinc-800/70",
    ].join(" ")}
  >
    <LayoutTemplate className="h-4 w-4" />
    {children}
  </button>
);

const Pill = ({ children, onRemove }: any) => (
  <span className="group inline-flex items-center gap-1 rounded-full border border-white/20 bg-white/50 px-2.5 py-1 text-xs text-zinc-700 dark:border-white/10 dark:bg-zinc-800/60 dark:text-zinc-200">
    {children}
    {onRemove && (
      <button
        onClick={onRemove}
        className="ml-1 opacity-0 transition group-hover:opacity-100"
      >
        ×
      </button>
    )}
  </span>
);

const EditableBullet = ({ bullet, onSave, onRephrase }: any) => {
  const [isEditing, setIsEditing] = useState(false);
  const [text, setText] = useState(bullet);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const handleSave = () => {
    onSave(text);
    setIsEditing(false);
  };

  const handleRephrase = async () => {
    setLoading(true);
    try {
      // Get 3 alternative phrasings
      const response = await axios.post(`${API_URL}/api/rephrase`, {
        bullet: text,
      });
      setSuggestions(response.data.alternatives || []);
      setShowSuggestions(true);
    } catch (error) {
      console.error("Rephrase error:", error);
    } finally {
      setLoading(false);
    }
  };

  if (isEditing) {
    return (
      <div className="space-y-2">
        <div className="flex gap-2">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="flex-1 rounded-lg border border-emerald-300 px-3 py-2 text-sm"
            autoFocus
          />
          <button
            onClick={handleSave}
            className="rounded-lg bg-emerald-500 px-3 py-2 text-white"
          >
            <Save className="h-4 w-4" />
          </button>
        </div>
        <button
          onClick={handleRephrase}
          disabled={loading}
          className="text-xs text-emerald-600 hover:underline disabled:opacity-50"
        >
          {loading ? "✨ Rephrasing..." : "✨ Get AI alternatives"}
        </button>
        {showSuggestions && suggestions.length > 0 && (
          <div className="space-y-1 rounded-lg bg-emerald-50 p-2 dark:bg-emerald-900/20">
            <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-300">
              Alternative phrasings:
            </p>
            {suggestions.map((s, idx) => (
              <button
                key={idx}
                onClick={() => {
                  setText(s);
                  setShowSuggestions(false);
                }}
                className="block w-full rounded bg-white p-2 text-left text-xs hover:bg-emerald-100 dark:bg-zinc-800 dark:hover:bg-zinc-700"
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <li className="group flex items-start justify-between gap-2 text-sm text-zinc-700 dark:text-zinc-300">
      <span>{text}</span>
      <button
        onClick={() => setIsEditing(true)}
        className="opacity-0 transition group-hover:opacity-100"
      >
        <Edit3 className="h-3 w-3 text-emerald-600" />
      </button>
    </li>
  );
};

const ResumeCard = ({ resume, experiences, skills, onBulletSave }: any) => {
  const { name, targetRole, template } = resume;

  const accent =
    template === "Modern"
      ? "from-emerald-400 to-cyan-400"
      : template === "Minimal"
      ? "from-zinc-300 to-zinc-100 dark:from-zinc-700 dark:to-zinc-800"
      : "from-emerald-500 to-emerald-400";

  return (
    <div className="rounded-2xl border border-white/10 bg-white p-6 shadow-xl dark:bg-zinc-950/80">
      {/* Header */}
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-extrabold tracking-tight text-zinc-900 dark:text-white">
            {name || "Your Name"}
          </h2>
          <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">
            {targetRole || "Target Role"}
          </p>
        </div>
        <div
          className="hidden rounded-2xl bg-gradient-to-r px-3 py-1.5 text-xs font-semibold text-zinc-900 dark:text-zinc-900 md:block"
          style={{
            backgroundImage:
              "linear-gradient(90deg, var(--tw-gradient-from), var(--tw-gradient-to))",
          }}
        >
          <span className={`bg-gradient-to-r ${accent} bg-clip-text text-transparent`}>
            RezzoMaker
          </span>
        </div>
      </div>

      {/* Skills */}
      {skills && skills.length > 0 && (
        <div className="mb-5 flex flex-wrap gap-2">
          {skills.map((skill: string, idx: number) => (
            <Pill key={idx}>{skill}</Pill>
          ))}
        </div>
      )}

      {/* Experiences */}
      <div className="space-y-4">
        {experiences && experiences.length > 0 ? (
          experiences.map((exp: Experience, idx: number) => (
            <div key={exp.id || idx} className="space-y-2">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                    {exp.jobTitle || "Job Title"} <span className="text-zinc-400">·</span>{" "}
                    {exp.company || "Company"}
                  </p>
                  {exp.bullets && exp.bullets.length > 0 && (
                    <ul className="mt-2 list-disc space-y-1 pl-5">
                      {exp.bullets.map((bullet, bulletIdx) => (
                        <EditableBullet
                          key={bulletIdx}
                          bullet={bullet}
                          onSave={(newText: string) => onBulletSave(idx, bulletIdx, newText)}
                          onRephrase={() => {}}
                        />
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <p className="text-center text-sm text-zinc-400">
            Start typing to see your AI-enhanced resume...
          </p>
        )}
      </div>
    </div>
  );
};

export default function RezzoMakerUI() {
  const [view, setView] = useState<"editor" | "list">("editor");
  const [resumes, setResumes] = useState<Resume[]>([]);
  const [currentResume, setCurrentResume] = useState<Resume>({
    id: "",
    name: "",
    targetRole: "",
    template: "Classic",
    experiences: [],
    skills: [],
    createdAt: Date.now(),
  });

  // Form state
  const [jobTitle, setJobTitle] = useState("");
  const [company, setCompany] = useState("");
  const [desc, setDesc] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [enhanceTimeout, setEnhanceTimeout] = useState<NodeJS.Timeout | null>(null);

  const resumeRef = useRef<HTMLDivElement>(null);

  // Debounced AI enhancement
  useEffect(() => {
    if (desc.length > 20 && currentResume.targetRole) {
      if (enhanceTimeout) clearTimeout(enhanceTimeout);

      const timeout = setTimeout(async () => {
        setAiBusy(true);
        try {
          const [enhanceRes, skillsRes] = await Promise.all([
            axios.post(`${API_URL}/api/enhance`, {
              brief: desc,
              targetRole: currentResume.targetRole,
            }),
            axios.post(`${API_URL}/api/skills`, {
              brief: desc,
              targetRole: currentResume.targetRole,
            }),
          ]);

          const newExp: Experience = {
            id: crypto.randomUUID(),
            jobTitle,
            company,
            brief: desc,
            bullets: enhanceRes.data.bullets,
          };

          setCurrentResume((prev) => ({
            ...prev,
            experiences: [newExp, ...prev.experiences.slice(1)],
            skills: skillsRes.data.skills,
          }));
        } catch (error) {
          console.error("Enhancement error:", error);
        } finally {
          setAiBusy(false);
        }
      }, 1500);

      setEnhanceTimeout(timeout);
    }

    return () => {
      if (enhanceTimeout) clearTimeout(enhanceTimeout);
    };
  }, [desc, currentResume.targetRole, jobTitle, company]);

  const addExperience = () => {
    if (currentResume.experiences.length > 0 && currentResume.experiences[0].bullets.length > 0) {
      // Clear form
      setJobTitle("");
      setCompany("");
      setDesc("");
    }
  };

  const saveResume = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/resume`, {
        userId: "demo-user",
        name: currentResume.name,
        targetRole: currentResume.targetRole,
        experiences: currentResume.experiences,
        skills: currentResume.skills,
      });

      const savedResume = {
        ...currentResume,
        id: response.data.resumeId,
      };

      setResumes((prev) => [savedResume, ...prev]);
      alert(`✅ Resume saved! ID: ${response.data.resumeId}`);
    } catch (error) {
      console.error("Save error:", error);
      alert("❌ Failed to save resume");
    }
  };

  const exportPDF = async () => {
    if (!resumeRef.current) return;

    try {
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      let yPos = margin;

      // Header
      pdf.setFontSize(24);
      pdf.setFont("helvetica", "bold");
      pdf.text(currentResume.name || "Your Name", margin, yPos);
      yPos += 8;

      pdf.setFontSize(14);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(100);
      pdf.text(currentResume.targetRole || "Target Role", margin, yPos);
      yPos += 10;

      // Skills
      if (currentResume.skills.length > 0) {
        pdf.setFontSize(12);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(0);
        pdf.text("Skills", margin, yPos);
        yPos += 6;

        pdf.setFontSize(10);
        pdf.setFont("helvetica", "normal");
        const skillsText = currentResume.skills.join(" • ");
        const skillsLines = pdf.splitTextToSize(skillsText, pageWidth - margin * 2);
        pdf.text(skillsLines, margin, yPos);
        yPos += skillsLines.length * 5 + 5;
      }

      // Experiences
      if (currentResume.experiences.length > 0) {
        pdf.setFontSize(12);
        pdf.setFont("helvetica", "bold");
        pdf.text("Experience", margin, yPos);
        yPos += 8;

        currentResume.experiences.forEach((exp) => {
          if (yPos > pageHeight - 30) {
            pdf.addPage();
            yPos = margin;
          }

          pdf.setFontSize(11);
          pdf.setFont("helvetica", "bold");
          pdf.setTextColor(0);
          pdf.text(`${exp.jobTitle} • ${exp.company}`, margin, yPos);
          yPos += 6;

          pdf.setFontSize(10);
          pdf.setFont("helvetica", "normal");
          exp.bullets.forEach((bullet) => {
            const lines = pdf.splitTextToSize(bullet, pageWidth - margin * 2 - 5);
            lines.forEach((line: string) => {
              if (yPos > pageHeight - 20) {
                pdf.addPage();
                yPos = margin;
              }
              pdf.text(line, margin + 5, yPos);
              yPos += 5;
            });
          });
          yPos += 5;
        });
      }

      // Footer
      pdf.setFontSize(8);
      pdf.setTextColor(150);
      pdf.text(
        "Generated with RezzoMaker™",
        pageWidth / 2,
        pageHeight - 10,
        { align: "center" }
      );

      pdf.save(`${currentResume.name || "resume"}_${currentResume.targetRole || "resume"}.pdf`);
    } catch (error) {
      console.error("PDF export error:", error);
      alert("❌ Failed to export PDF");
    }
  };

  const createNewResume = () => {
    setCurrentResume({
      id: "",
      name: "",
      targetRole: "",
      template: "Classic",
      experiences: [],
      skills: [],
      createdAt: Date.now(),
    });
    setJobTitle("");
    setCompany("");
    setDesc("");
    setView("editor");
  };

  const duplicateResume = (resume: Resume) => {
    setCurrentResume({
      ...resume,
      id: "",
      name: `${resume.name} (Copy)`,
      createdAt: Date.now(),
    });
    setView("editor");
  };

  const deleteResume = (id: string) => {
    setResumes((prev) => prev.filter((r) => r.id !== id));
  };

  const calculateScore = () => {
    let score = 0;
    if (currentResume.name) score += 10;
    if (currentResume.targetRole) score += 10;
    if (currentResume.experiences.length > 0) score += 40;
    if (currentResume.skills.length > 0) score += 20;
    if (currentResume.experiences.some((e) => e.bullets && e.bullets.length > 0)) score += 20;
    return score;
  };

  const handleBulletSave = (expIdx: number, bulletIdx: number, newText: string) => {
    setCurrentResume((prev) => {
      const newExperiences = [...prev.experiences];
      newExperiences[expIdx].bullets[bulletIdx] = newText;
      return { ...prev, experiences: newExperiences };
    });
  };

  if (view === "list") {
    return (
      <div className="min-h-screen w-full bg-[radial-gradient(1200px_600px_at_20%_-10%,rgba(16,185,129,0.20),transparent),radial-gradient(900px_500px_at_80%_0%,rgba(59,130,246,0.15),transparent)] px-4 py-8 text-zinc-900 dark:bg-zinc-950 dark:text-zinc-50">
        <div className="mx-auto max-w-4xl">
          <div className="mb-8 flex items-center justify-between">
            <h1 className="text-3xl font-bold">My Resumes</h1>
            <button
              onClick={createNewResume}
              className="flex items-center gap-2 rounded-xl bg-emerald-500 px-4 py-2 text-white shadow-lg hover:bg-emerald-600"
            >
              <Plus className="h-4 w-4" /> New Resume
            </button>
          </div>

          <div className="space-y-4">
            {resumes.map((resume) => (
              <div
                key={resume.id}
                className="flex items-center justify-between rounded-2xl border border-white/10 bg-white/60 p-4 backdrop-blur"
              >
                <div className="flex-1">
                  <h3 className="font-semibold">{resume.name}</h3>
                  <p className="text-sm text-zinc-600">{resume.targetRole}</p>
                  <p className="text-xs text-zinc-400">
                    {resume.experiences.length} experiences • {resume.skills.length} skills
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => duplicateResume(resume)}
                    className="rounded-lg border border-white/10 bg-white/40 p-2 hover:bg-white/70"
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => {
                      setCurrentResume(resume);
                      setView("editor");
                    }}
                    className="rounded-lg border border-white/10 bg-white/40 p-2 hover:bg-white/70"
                  >
                    <Edit3 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => deleteResume(resume.id)}
                    className="rounded-lg border border-red-200 bg-red-50 p-2 hover:bg-red-100"
                  >
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </button>
                </div>
              </div>
            ))}

            {resumes.length === 0 && (
              <div className="rounded-2xl border border-white/10 bg-white/60 p-12 text-center backdrop-blur">
                <p className="text-zinc-600">No resumes yet. Create your first one!</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-[radial-gradient(1200px_600px_at_20%_-10%,rgba(16,185,129,0.20),transparent),radial-gradient(900px_500px_at_80%_0%,rgba(59,130,246,0.15),transparent)] px-4 py-8 text-zinc-900 dark:bg-zinc-950 dark:text-zinc-50">
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-8 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/60 px-3 py-1 text-xs text-zinc-700 shadow-sm backdrop-blur dark:border-white/10 dark:bg-zinc-900/60 dark:text-zinc-200">
              <Stars className="h-3 w-3" /> Powered by AI · RezzoMaker
            </div>
            <h1 className="mt-3 text-2xl font-extrabold tracking-tight sm:text-3xl">
              Build a resume that **works** — fast.
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-zinc-600 dark:text-zinc-300">
              Fill a few fields. Let RezzoMaker rewrite, quantify, and style your story for the
              role you want.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setView("list")}
              className="flex items-center gap-2 rounded-xl border border-white/15 bg-white/60 px-3 py-2 text-sm font-medium hover:bg-white/80"
            >
              <List className="h-4 w-4" /> My Resumes
            </button>
            <div className="flex items-center gap-2 text-xs text-zinc-500 dark:text-zinc-400">
              <Gauge className="h-4 w-4" />
              <span>Score</span>
              <span className="rounded-full bg-emerald-500/20 px-2 py-0.5 font-semibold text-emerald-600 dark:text-emerald-300">
                {calculateScore()}
              </span>
            </div>
          </div>
        </div>

        {/* Grid */}
        <div className="grid gap-6 md:grid-cols-2">
          {/* Left: Form */}
          <Section
            title="Add Experience"
            desc="Keep it brief. We'll enhance wording, add metrics, and map keywords to the job."
            icon={<Pencil className="h-5 w-5 text-emerald-500" />}
          >
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={labelBase}>Your Name</label>
                  <div className="mt-1 flex items-center gap-3 rounded-xl border border-white/15 bg-white/50 p-2.5 dark:border-white/10 dark:bg-zinc-900/50">
                    <FileText className="h-4 w-4 text-zinc-400" />
                    <input
                      className={fieldBase}
                      placeholder="e.g. Alex Carter"
                      value={currentResume.name}
                      onChange={(e) =>
                        setCurrentResume((prev) => ({ ...prev, name: e.target.value }))
                      }
                    />
                  </div>
                </div>
                <div>
                  <label className={labelBase}>Target Role</label>
                  <div className="mt-1 flex items-center gap-3 rounded-xl border border-white/15 bg-white/50 p-2.5 dark:border-white/10 dark:bg-zinc-900/50">
                    <Briefcase className="h-4 w-4 text-zinc-400" />
                    <input
                      className={fieldBase}
                      placeholder="e.g. Product Manager"
                      value={currentResume.targetRole}
                      onChange={(e) =>
                        setCurrentResume((prev) => ({ ...prev, targetRole: e.target.value }))
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={labelBase}>Company</label>
                  <div className="mt-1 flex items-center gap-3 rounded-xl border border-white/15 bg-white/50 p-2.5 dark:border-white/10 dark:bg-zinc-900/50">
                    <Building2 className="h-4 w-4 text-zinc-400" />
                    <input
                      className={fieldBase}
                      placeholder="e.g. Rezzo.work"
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                    />
                  </div>
                </div>
                <div>
                  <label className={labelBase}>Job Title</label>
                  <div className="mt-1 flex items-center gap-3 rounded-xl border border-white/15 bg-white/50 p-2.5 dark:border-white/10 dark:bg-zinc-900/50">
                    <Briefcase className="h-4 w-4 text-zinc-400" />
                    <input
                      className={fieldBase}
                      placeholder="e.g. Senior Engineer"
                      value={jobTitle}
                      onChange={(e) => setJobTitle(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className={labelBase}>
                  What did you do? (brief)
                  {aiBusy && <span className="ml-2 text-emerald-500">✨ AI enhancing...</span>}
                </label>
                <div className="mt-1 rounded-2xl border border-white/15 bg-white/50 p-2.5 dark:border-white/10 dark:bg-zinc-900/50">
                  <textarea
                    rows={4}
                    className={`${fieldBase} resize-none`}
                    placeholder="Led redesign of onboarding; partnered with data to cut time-to-value; owned roadmap, wrote PRDs…"
                    value={desc}
                    onChange={(e) => setDesc(e.target.value)}
                  />
                </div>
                <div className="mt-2 flex items-center gap-2 text-xs text-zinc-500 dark:text-zinc-400">
                  <Info className="h-4 w-4" /> Use plain language. We'll turn it into impact
                  bullets with numbers.
                </div>
              </div>

              <div>
                <label className={labelBase}>Template</label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {(["Classic", "Modern", "Minimal"] as const).map((t) => (
                    <TemplateBadge
                      key={t}
                      active={currentResume.template === t}
                      onClick={() => setCurrentResume((prev) => ({ ...prev, template: t }))}
                    >
                      {t}
                    </TemplateBadge>
                  ))}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3 pt-2">
                <button
                  onClick={addExperience}
                  className="inline-flex items-center gap-2 rounded-xl border border-emerald-400/40 bg-emerald-500/10 px-4 py-2 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-500/20 dark:text-emerald-300"
                >
                  <Plus className="h-4 w-4" /> Add Experience
                </button>
                <button
                  onClick={saveResume}
                  className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-emerald-500/25 transition hover:bg-emerald-600"
                >
                  <Save className="h-4 w-4" /> Save Resume
                </button>
              </div>
            </div>
          </Section>

          {/* Right: Preview */}
          <Section title="Preview" icon={<Eye className="h-5 w-5 text-emerald-500" />}>
            <motion.div
              ref={resumeRef}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <ResumeCard
                resume={currentResume}
                experiences={currentResume.experiences}
                skills={currentResume.skills}
                onBulletSave={handleBulletSave}
              />
            </motion.div>

            <div className="mt-4 flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs text-zinc-500 dark:text-zinc-400">
                <Stars className="h-4 w-4" /> AI will add quantified bullets
              </div>
              <button
                onClick={exportPDF}
                className="inline-flex items-center gap-2 rounded-lg border border-white/15 bg-white/60 px-3 py-1.5 text-xs font-medium text-zinc-800 hover:bg-white/80 dark:bg-zinc-900/60 dark:text-zinc-100"
              >
                <Download className="h-4 w-4" /> Export PDF
              </button>
            </div>
          </Section>
        </div>

        {/* Footer */}
        <div className="mx-auto mt-8 max-w-3xl rounded-2xl border border-white/10 bg-white/50 p-4 text-center text-sm text-zinc-700 backdrop-blur dark:bg-zinc-900/50 dark:text-zinc-300">
          Pro tip: Paste any job description and click <span className="font-semibold">Save</span>.
          RezzoMaker aligns keywords, rewrites bullets, and suggests a customized summary.
        </div>
      </div>
    </div>
  );
}
